import React, { useEffect, useState, useRef } from "react";
import {
  Box, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, IconButton, Dialog, DialogContent, DialogTitle, Tooltip, Card, CardContent,
  Alert, Snackbar,
} from "@mui/material";
import {
  Add as AddIcon, Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon,
  Print as PrintIcon, Refresh as RefreshIcon, Receipt as ReceiptIcon, AttachMoney as MoneyIcon,
  CheckCircle as PaidIcon, HourglassEmpty as PendingIconMui, Share as ShareIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { invokeApi, apiList } from "../../../services/apiServices";
import { config } from "../../../config/config";
import { Cookies } from "react-cookie";
import { useUser } from "../../../context/UserContext";
import { InvoiceDocument, InvoiceModal, printInvoiceElement, PURPLE, formatCurrency, formatDate } from "../../../componants/common/InvoiceDocument";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";

const BORDER = "#c8c8e0";
const TEXT_MED = "#555555";

// ── Stat Card Component ────────────────────────────────────────────────────────
const StatCard = ({ title, value, icon, color, loading }) => (
  <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, height: "100%" }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, color: TEXT_MED, fontSize: "10px" }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, color }}>
            {loading ? <CircularProgress size={20} sx={{ color }} /> : value}
          </Typography>
        </Box>
        <Box sx={{ p: 1.2, bgcolor: `${color}18`, borderRadius: 2 }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const InvoiceDashboard = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const rawRoles = user?.roles || user?.role || [];
  const userRoles = Array.isArray(rawRoles)
    ? rawRoles
    : typeof rawRoles === "string"
      ? rawRoles.split(",").map((r) => r.trim())
      : [];
  const isSuperAdmin = userRoles.includes("Super Admin");
  const isAdminOrSuperAdmin = userRoles.some((r) => ["Super Admin", "Admin", "Manager", "Employee"].includes(r)) || !user?.id;

  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [emailSuccessMsg, setEmailSuccessMsg] = useState("");
  const printRef = useRef();

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, []);

  // Fetch list — staff sees all company invoices
  const fetchInvoices = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const payload = {};
      if (search && search.trim()) payload.search = search.trim();
      if (statusFilter && statusFilter.trim()) payload.paymentStatus = statusFilter.trim();
      if (fromDate) payload.fromDate = fromDate;
      if (toDate) payload.toDate = toDate;

      const res = await invokeApi(apiList.getInvoices, payload, Cookies);
      if (res?.status === 200 || res?.data?.responseCode === "200" || res?.data?.status === 200) {
        const list = res.data?.data?.invoices || res.data?.invoices || (Array.isArray(res.data) ? res.data : []);
        setInvoices(list);
      } else {
        setFetchError(res?.data?.responseMessage || res?.data?.error || "Failed to load invoices.");
      }
    } catch (err) {
      setFetchError("Network error occurred while fetching invoices.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const res = await invokeApi(apiList.getInvoiceStats, {}, Cookies);
      if (res?.status === 200 || res?.data?.responseCode === "200" || res?.data?.status === 200) {
        setStats(res.data?.data?.summary || res.data?.summary || res.data?.data || null);
      }
    } catch (err) {
      // stats fallback
    } finally {
      setStatsLoading(false);
    }
  };

  // Dispatch invoice email confirmation to customer
  const handleSendEmail = async (inv) => {
    if (!inv?.customer_email) {
      alert("No email address found for this invoice. Please provide a customer email address.");
      return;
    }
    setSendingEmailId(inv.id);
    try {
      const res = await invokeApi(apiList.sendInvoiceEmail, { invoiceId: inv.id, email: inv.customer_email }, Cookies);
      if (res?.status === 200 || res?.data?.responseCode === "200") {
        setEmailSuccessMsg(`✓ Invoice confirmation sent to ${inv.customer_email}`);
      } else {
        alert(res?.data?.responseMessage || "Failed to send invoice email.");
      }
    } catch {
      alert("Error sending invoice email.");
    } finally {
      setSendingEmailId(null);
    }
  };

  // View single invoice
  const handleView = async (id) => {
    setViewLoading(true);
    try {
      const res = await invokeApi(apiList.getInvoice, { invoiceId: id }, Cookies);
      if (res?.status === 200) {
        setViewInvoice(res.data?.invoice || res.data?.data?.invoice || null);
      }
    } finally {
      setViewLoading(false);
    }
  };

  // Status update
  const handleStatusChange = async (id, newStatus) => {
    await invokeApi(
      apiList.updateInvoiceStatus,
      { invoiceId: id, paymentStatus: newStatus },
      Cookies
    );
    fetchInvoices();
    fetchStats();
    if (viewInvoice?.id === id) setViewInvoice((prev) => ({ ...prev, payment_status: newStatus }));
  };

  // Delete
  const handleDelete = async (id) => {
    await invokeApi(apiList.deleteInvoice, { invoiceId: id }, Cookies);
    setConfirmDelete(null);
    setViewInvoice(null);
    fetchInvoices();
    fetchStats();
  };

  // Share link
  const handleShare = (token) => {
    const url = `${window.location.origin}/invoice/${token}`;
    navigator.clipboard.writeText(url).then(() => alert("Public invoice link copied to clipboard!"));
  };

  return (
    <Box sx={{ display: "flex", bgcolor: "#f4f4f8", minHeight: "100vh" }}>
      <Sidenav />
      <Box component="main" sx={{ flexGrow: 1, p: 2 }}>
        <Header />
        <Box sx={{ mt: 2 }}>
      {/* ── Header ── */}
      <Box sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1.5,
        mb: 2,
      }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: PURPLE }}>
            Invoice Manager
          </Typography>
          <Typography variant="body2" sx={{ color: TEXT_MED }}>
            {isSuperAdmin ? "Super Admin — All Invoices" : "Admin — My Invoices"}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/invoice-manager/create-invoice")}
          sx={{
            backgroundColor: PURPLE,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            "&:hover": { backgroundColor: "#5c5fa0" },
          }}
        >
          New Invoice
        </Button>
      </Box>

      {/* ── Stats Cards ── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          { title: "Total Invoices", value: stats?.total_invoices || invoices.length || 0, icon: <ReceiptIcon />, color: PURPLE },
          { title: "Total Amount", value: `₹ ${formatCurrency(stats?.total_amount || 0)}`, icon: <MoneyIcon />, color: "#2e7d32" },
          { title: "Paid", value: stats?.paid_count || 0, icon: <PaidIcon />, color: "#1565c0" },
          { title: "Pending", value: stats?.pending_count || 0, icon: <PendingIconMui />, color: "#e65100" },
        ].map((s) => (
          <Grid item xs={6} sm={3} key={s.title}>
            <StatCard {...s} loading={statsLoading} />
          </Grid>
        ))}
      </Grid>

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Search name / invoice no / phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All</MenuItem>
                {["Paid", "Pending", "Partial", "Cancelled"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={6} sm={2} sx={{ display: "flex", gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchInvoices}
              sx={{
                backgroundColor: PURPLE,
                borderRadius: 2,
                textTransform: "none",
                "&:hover": { backgroundColor: "#5c5fa0" },
              }}
            >
              Search
            </Button>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => {
                  setSearch("");
                  setStatusFilter("");
                  setFromDate("");
                  setToDate("");
                  fetchInvoices();
                  fetchStats();
                }}
                sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Error Notice ── */}
      {fetchError && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => { fetchInvoices(); fetchStats(); }}>
              Retry
            </Button>
          }
        >
          {fetchError}
        </Alert>
      )}

      {/* ── Email Success Notice ── */}
      <Snackbar
        open={!!emailSuccessMsg}
        autoHideDuration={4000}
        onClose={() => setEmailSuccessMsg("")}
        message={emailSuccessMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* ── Table ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress sx={{ color: PURPLE }} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: PURPLE }}>
                  {[
                    "#",
                    "Invoice No.",
                    "Customer",
                    "Phone",
                    "Date",
                    "Total",
                    "Received",
                    "Balance",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "12px",
                        padding: "8px 10px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: "center", py: 4, color: "#888" }}>
                      No invoices found
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((inv, idx) => (
                    <TableRow key={inv.id} hover sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafafa" } }}>
                      <TableCell sx={{ fontSize: "12px", padding: "6px 10px" }}>{idx + 1}</TableCell>
                      <TableCell
                        sx={{
                          fontSize: "12px",
                          fontWeight: 700,
                          color: PURPLE,
                          padding: "6px 10px",
                        }}
                      >
                        {inv.invoice_number || inv.invoiceNumber}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", padding: "6px 10px" }}>
                        {inv.customer_name || inv.customerName}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", padding: "6px 10px" }}>
                        {inv.customer_number || inv.customerNumber}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", padding: "6px 10px", whiteSpace: "nowrap" }}>
                        {formatDate(inv.created_at || inv.dueDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", padding: "6px 10px", fontWeight: 600 }}>
                        ₹ {formatCurrency(inv.total)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", padding: "6px 10px" }}>
                        ₹ {formatCurrency(inv.received)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", padding: "6px 10px" }}>
                        ₹ {formatCurrency(inv.balance)}
                      </TableCell>
                      <TableCell sx={{ padding: "6px 10px" }}>
                        <Select
                          size="small"
                          value={inv.payment_status || "Pending"}
                          onChange={(e) => handleStatusChange(inv.id, e.target.value)}
                          sx={{
                            fontSize: "11px",
                            height: "26px",
                            borderRadius: 4,
                            color:
                              {
                                Paid: "#2e7d32",
                                Pending: "#e65100",
                                Partial: "#1565c0",
                                Cancelled: "#c62828",
                              }[inv.payment_status] || "#555",
                          }}
                        >
                          {["Paid", "Pending", "Partial", "Cancelled"].map((s) => (
                            <MenuItem key={s} value={s} sx={{ fontSize: "12px" }}>
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell sx={{ padding: "4px 8px", whiteSpace: "nowrap" }}>
                        <Tooltip title="View & Print">
                          <IconButton size="small" onClick={() => handleView(inv.id)} sx={{ color: PURPLE }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {inv.share_token && (
                          <Tooltip title="Share Link">
                            <IconButton
                              size="small"
                              onClick={() => handleShare(inv.share_token)}
                              sx={{ color: "#2e7d32" }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={inv.customer_email ? `Email invoice to ${inv.customer_email}` : "No email available"}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!inv.customer_email || sendingEmailId === inv.id}
                              onClick={() => handleSendEmail(inv)}
                              sx={{ color: "#1976d2" }}
                            >
                              {sendingEmailId === inv.id ? <CircularProgress size={16} /> : <EmailIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {isSuperAdmin && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setConfirmDelete(inv.id)}
                              sx={{ color: "#c62828" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── View & Print Invoice Modal ── */}
      <InvoiceModal
        open={!!viewInvoice}
        onClose={() => setViewInvoice(null)}
        data={viewInvoice}
      />

      {/* ── Confirm Delete Dialog ── */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Invoice?</DialogTitle>
        <DialogContent>
          <Typography>This action will soft-delete the invoice. It can be restored later by Super Admin.</Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => setConfirmDelete(null)} variant="outlined" sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(confirmDelete)}
              variant="contained"
              sx={{
                backgroundColor: "#c62828",
                textTransform: "none",
                "&:hover": { backgroundColor: "#8e0000" },
              }}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceDashboard;
